import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Pencil, Trash2, X, ChevronLeft, ChevronRight, User, Download } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Textarea } from '../../components/ui/textarea';
import { toast } from 'sonner';
import DraggableModal from '../../components/ui/DraggableModal';
import { adminFetch } from '../../utils/adminFetch';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const services = ['Recién Nacido', 'Primera Comunión', 'Boda', 'Embarazo', 'Familia', 'Bebés', 'Bodas', 'Publicidad'];
const statusLabels = { pending: 'Pendiente', confirmed: 'Confirmado', completed: 'Completado', cancelled: 'Cancelado' };
const statusColors = { pending: 'bg-amber-500', confirmed: 'bg-green-500', completed: 'bg-blue-500', cancelled: 'bg-gray-500' };

// Service type colors for calendar
const serviceColors = {
  'Recién Nacido': 'bg-blue-500',
  'Primera Comunión': 'bg-purple-500',
  'Boda': 'bg-rose-500',
  'Bodas': 'bg-rose-500',
  'Embarazo': 'bg-pink-500',
  'Familia': 'bg-green-500',
  'Bebés': 'bg-blue-400',
  'Publicidad': 'bg-orange-500',
};

export const AdminCalendar = () => {
  const [events, setEvents] = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [form, setForm] = useState({ title: '', client_id: '', client_name: '', service_type: services[0], date: '', time: '10:00', duration: 60, location: '', notes: '', status: 'confirmed' });

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    try {
      const [eventsRes, clientsRes] = await Promise.all([
        adminFetch(`${API}/admin/calendar`),
        adminFetch(`${API}/admin/clients`)
      ]);
      if (eventsRes.ok) setEvents(await eventsRes.json());
      if (clientsRes.ok) setClients(await clientsRes.json());
    } catch (error) {
      toast.error('Error al cargar datos');
    } finally {
      setLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      const res = await adminFetch(`${API}/admin/calendar-export`);
      if (!res.ok) throw new Error('Error');
      const data = await res.json();
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `eventos_${new Date().toISOString().slice(0, 10)}.json`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success(`${data.length} evento(s) exportado(s)`);
    } catch {
      toast.error('Error al exportar eventos');
    }
  };


  const getDaysInMonth = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1).getDay();
    const daysInMonth = new Date(year, month + 1, 0).getDate();
    return { firstDay: firstDay === 0 ? 6 : firstDay - 1, daysInMonth };
  };

  const { firstDay, daysInMonth } = getDaysInMonth(currentDate);
  const monthStr = currentDate.toLocaleDateString('es-ES', { month: 'long', year: 'numeric' });

  const getEventsForDay = (day) => {
    const dateStr = `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
    return events.filter(e => e.date === dateStr);
  };

  const openModal = (event = null, day = null) => {
    if (event) {
      setEditing(event);
      setForm({ ...event });
    } else {
      setEditing(null);
      const dateStr = day 
        ? `${currentDate.getFullYear()}-${String(currentDate.getMonth() + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`
        : new Date().toISOString().split('T')[0];
      setForm({ title: '', client_id: '', client_name: '', service_type: services[0], date: dateStr, time: '10:00', duration: 60, location: '', notes: '', status: 'confirmed' });
    }
    setShowModal(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.date) { toast.error('Título y fecha son obligatorios'); return; }
    try {
      const url = editing ? `${API}/admin/calendar/${editing.event_id}` : `${API}/admin/calendar`;
      const response = await adminFetch(url, {
        method: editing ? 'PUT' : 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form)
      });
      if (!response.ok) throw new Error('Error saving');
      toast.success(editing ? 'Evento actualizado' : 'Evento creado');
      setShowModal(false);
      fetchData();
    } catch (error) {
      toast.error('Error al guardar');
    }
  };

  const handleDelete = async (id) => {
    if (!confirm('¿Eliminar este evento?')) return;
    try {
      await adminFetch(`${API}/admin/calendar/${id}`, { method: 'DELETE' });
      toast.success('Evento eliminado');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const prevMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() - 1));
  const nextMonth = () => setCurrentDate(new Date(currentDate.getFullYear(), currentDate.getMonth() + 1));

  const weekDays = ['Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb', 'Dom'];
  const today = new Date();
  const isToday = (day) => today.getDate() === day && today.getMonth() === currentDate.getMonth() && today.getFullYear() === currentDate.getFullYear();

  return (
    <div className="p-6 lg:p-8">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="font-serif text-3xl mb-2">Calendario</h1>
            <p className="text-muted-foreground">Gestiona tus sesiones y eventos</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExport} className="gap-2" data-testid="export-calendar-btn">
              <Download className="w-4 h-4" /> Exportar JSON
            </Button>
            <Button onClick={() => openModal()} className="gap-2">
              <Plus className="w-4 h-4" /> Nuevo Evento
            </Button>
          </div>
        </div>

        {/* Calendar Header */}
        <div className="bg-card border border-border mb-6">
          <div className="p-4 flex items-center justify-between border-b border-border">
            <button onClick={prevMonth} className="p-2 hover:bg-muted rounded"><ChevronLeft className="w-5 h-5" /></button>
            <h2 className="font-serif text-xl capitalize">{monthStr}</h2>
            <button onClick={nextMonth} className="p-2 hover:bg-muted rounded"><ChevronRight className="w-5 h-5" /></button>
          </div>

          {/* Week days header */}
          <div className="grid grid-cols-7 border-b border-border">
            {weekDays.map(day => (
              <div key={day} className="p-2 text-center text-xs font-medium text-muted-foreground">{day}</div>
            ))}
          </div>

          {/* Calendar grid */}
          <div className="grid grid-cols-7">
            {/* Empty cells for days before month starts */}
            {[...Array(firstDay)].map((_, i) => (
              <div key={`empty-${i}`} className="min-h-[100px] p-2 border-b border-r border-border bg-muted/30" />
            ))}
            
            {/* Days of the month */}
            {[...Array(daysInMonth)].map((_, i) => {
              const day = i + 1;
              const dayEvents = getEventsForDay(day);
              return (
                <div 
                  key={day} 
                  className={`min-h-[100px] p-2 border-b border-r border-border cursor-pointer hover:bg-muted/50 transition-colors ${isToday(day) ? 'bg-muted' : ''}`}
                  onClick={() => openModal(null, day)}
                >
                  <span className={`text-sm ${isToday(day) ? 'w-6 h-6 bg-foreground text-background rounded-full flex items-center justify-center' : ''}`}>
                    {day}
                  </span>
                  <div className="mt-1 space-y-1">
                    {dayEvents.slice(0, 3).map(event => (
                      <div 
                        key={event.event_id}
                        onClick={(e) => { e.stopPropagation(); openModal(event); }}
                        className={`text-xs p-1 truncate rounded ${serviceColors[event.service_type] || statusColors[event.status]} text-white cursor-pointer`}
                        title={`${event.title}${event.client_name ? ` - ${event.client_name}` : ''}`}
                      >
                        {event.time} {event.title}
                      </div>
                    ))}
                    {dayEvents.length > 3 && <span className="text-xs text-muted-foreground">+{dayEvents.length - 3} más</span>}
                  </div>
                </div>
              );
            })}
          </div>

          {/* Legend */}
          <div className="p-3 border-t border-border flex flex-wrap gap-3 text-xs">
            <span className="text-muted-foreground">Tipos de sesión:</span>
            {Object.entries(serviceColors).slice(0, 6).map(([type, color]) => (
              <span key={type} className="flex items-center gap-1">
                <span className={`w-2 h-2 rounded-full ${color}`} />
                {type}
              </span>
            ))}
          </div>
        </div>

        {/* Upcoming Events List */}
        <div className="bg-card border border-border">
          <div className="p-4 border-b border-border">
            <h3 className="font-medium">Próximos eventos</h3>
          </div>
          <div className="divide-y divide-border">
            {events
              .filter(e => e.date >= new Date().toISOString().split('T')[0] && e.status !== 'cancelled')
              .slice(0, 5)
              .map(event => (
                <div key={event.event_id} className="p-4 flex items-center gap-4 group">
                  <div className={`w-3 h-3 rounded-full ${serviceColors[event.service_type] || statusColors[event.status]}`} />
                  <div className="flex-1">
                    <p className="font-medium">{event.title}</p>
                    <p className="text-sm text-muted-foreground">{event.service_type} • {event.location}</p>
                    {event.client_name && (
                      <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
                        <User className="w-3 h-3" />
                        {event.client_name}
                      </p>
                    )}
                  </div>
                  <div className="text-right">
                    <p className="font-medium">{event.date}</p>
                    <p className="text-sm text-muted-foreground">{event.time}</p>
                  </div>
                  <div className="flex gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                    <button onClick={() => openModal(event)} className="p-2 hover:bg-muted rounded"><Pencil className="w-4 h-4" /></button>
                    <button onClick={() => handleDelete(event.event_id)} className="p-2 hover:bg-muted rounded"><Trash2 className="w-4 h-4 text-destructive" /></button>
                  </div>
                </div>
              ))}
            {events.filter(e => e.date >= new Date().toISOString().split('T')[0]).length === 0 && (
              <div className="p-8 text-center text-muted-foreground">No hay eventos próximos</div>
            )}
          </div>
        </div>
      </motion.div>

      {/* Event Modal */}
      {showModal && (
        <DraggableModal onClose={() => setShowModal(false)}>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-background border border-border w-[28rem] max-w-[calc(100vw-2rem)] max-h-[90vh] overflow-y-auto">
            <div className="p-4 border-b border-border flex items-center justify-between">
              <h2 className="font-medium">{editing ? 'Editar Evento' : 'Nuevo Evento'}</h2>
              <button onClick={() => setShowModal(false)}><X className="w-5 h-5" /></button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Título *</label>
                <Input value={form.title} onChange={e => setForm({...form, title: e.target.value})} placeholder="Ej: Sesión de fotos bebé" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Cliente</label>
                <select 
                  value={form.client_id} 
                  onChange={e => {
                    const client = clients.find(c => c.client_id === e.target.value);
                    setForm({...form, client_id: e.target.value, client_name: client?.name || ''});
                  }}
                  className="w-full h-10 px-3 border border-input bg-background rounded-md"
                >
                  <option value="">-- Sin cliente asociado --</option>
                  {clients.map(c => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Tipo de servicio</label>
                <select value={form.service_type} onChange={e => setForm({...form, service_type: e.target.value})} className="w-full h-10 px-3 border border-input bg-background rounded-md">
                  {services.map(s => <option key={s} value={s}>{s}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Fecha *</label>
                  <Input type="date" value={form.date} onChange={e => setForm({...form, date: e.target.value})} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Hora</label>
                  <Input type="time" value={form.time} onChange={e => setForm({...form, time: e.target.value})} />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Duración (min)</label>
                  <Input type="number" value={form.duration} onChange={e => setForm({...form, duration: parseInt(e.target.value)})} />
                </div>
                <div>
                  <label className="text-xs text-muted-foreground block mb-1">Estado</label>
                  <select value={form.status} onChange={e => setForm({...form, status: e.target.value})} className="w-full h-10 px-3 border border-input bg-background rounded-md">
                    {Object.entries(statusLabels).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Ubicación</label>
                <Input value={form.location} onChange={e => setForm({...form, location: e.target.value})} placeholder="Estudio / Exterior / Dirección" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground block mb-1">Notas</label>
                <Textarea value={form.notes} onChange={e => setForm({...form, notes: e.target.value})} rows={2} />
              </div>
              <div className="flex justify-between gap-2 pt-4">
                <div>
                  {editing && (
                    <Button 
                      type="button" 
                      variant="destructive" 
                      onClick={() => {
                        handleDelete(editing.event_id);
                        setShowModal(false);
                      }}
                      className="gap-2"
                    >
                      <Trash2 className="w-4 h-4" />
                      Eliminar
                    </Button>
                  )}
                </div>
                <div className="flex gap-2">
                  <Button type="button" variant="outline" onClick={() => setShowModal(false)}>Cancelar</Button>
                  <Button type="submit">Guardar</Button>
                </div>
              </div>
            </form>
          </motion.div>
        </DraggableModal>
      )}
    </div>
  );
};

export default AdminCalendar;
